/**
 * Mobile Payment Service — RevenueCat Integration
 * 
 * Handles native in-app purchases and subscriptions via RevenueCat SDK.
 * On web, falls back to Stripe (handled separately in stripeService.ts).
 */

import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL, PURCHASES_ERROR_CODE } from '@revenuecat/purchases-capacitor';
import type { PurchasesOfferings, PurchasesPackage, CustomerInfo } from '@revenuecat/purchases-capacitor';
import { PlanTier } from '../types';

// ===== Platform Detection =====
export const isMobilePlatform = (): boolean => Capacitor.isNativePlatform();
export const getPlatform = (): 'android' | 'ios' | 'web' => {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') return 'android';
    if (platform === 'ios') return 'ios';
    return 'web';
};

// ===== RevenueCat Configuration =====
const REVENUECAT_API_KEY = 'test_RyLaHpEcAmKUbichpCpwplIBSWi';

// ===== Plan ↔ RevenueCat Mapping =====
// Each plan tier maps to a RevenueCat offering and/or product identifier.
//
// RevenueCat setup:
//   Offering "Starter"       → Product "YourNovel_Starter"       (€19/mes)
//   Offering "Professional"  → Product "YourNovel_Author_pro"    (€39/mes)
//   Offering "Agency"        → Product "YourNovel_Agency"        (€89/mes)
//   Default offering         → Product "YourNovel_Single_Book"   (€19 una vez)
//
// Entitlements:
//   "YourNovel Escritor Novel"  → granted by YourNovel_Starter
//   "YourNovel Author Pro"      → granted by YourNovel_Author_pro
//   "YourNovel Agency"          → granted by YourNovel_Agency

interface PlanConfig {
    offeringId: string;        // RevenueCat offering identifier
    entitlementId: string;     // RevenueCat entitlement identifier
    productIds: string[];      // Google Play / App Store product identifiers (fallback matching)
}

const PLAN_CONFIG: Record<string, PlanConfig> = {
    starter: {
        offeringId: 'Starter',
        entitlementId: 'YourNovel Escritor Novel',
        productIds: ['YourNovel_Starter', 'yournovel_starter_monthly'],
    },
    professional: {
        offeringId: 'Professional',
        entitlementId: 'YourNovel Author Pro',
        productIds: ['YourNovel_Author_pro', 'yournovel_pro_monthly'],
    },
    agency: {
        offeringId: 'Agency',
        entitlementId: 'YourNovel Agency',
        productIds: ['YourNovel_Agency', 'yournovel_agency_monthly'],
    },
};

// Map any active entitlement back to a PlanTier
const ENTITLEMENT_TO_PLAN: Record<string, PlanTier> = {
    'YourNovel Escritor Novel': 'starter',
    'YourNovel Author Pro': 'professional',
    'YourNovel Agency': 'agency',
};

let isRevenueCatInitialized = false;

/**
 * Initialize RevenueCat SDK. Call once on app startup (native only).
 */
export async function initializeMobilePayments(userId?: string): Promise<void> {
    if (!isMobilePlatform() || isRevenueCatInitialized) return;

    try {
        // Enable debug logging during development
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

        await Purchases.configure({
            apiKey: REVENUECAT_API_KEY,
            appUserID: userId || undefined,
        });

        // Identify user if logged in
        if (userId) {
            await Purchases.logIn({ appUserID: userId });
        }

        isRevenueCatInitialized = true;
        console.log('[RevenueCat] Initialized successfully');
    } catch (error) {
        console.error('[RevenueCat] Init failed:', error);
    }
}

/**
 * Identify a user in RevenueCat (call after login/registration).
 */
export async function identifyUser(userId: string): Promise<void> {
    if (!isRevenueCatInitialized) return;
    try {
        await Purchases.logIn({ appUserID: userId });
        console.log('[RevenueCat] User identified:', userId);
    } catch (error) {
        console.error('[RevenueCat] Identify failed:', error);
    }
}

/**
 * Log out from RevenueCat (call on app logout).
 */
export async function logOutRevenueCat(): Promise<void> {
    if (!isRevenueCatInitialized) return;
    try {
        await Purchases.logOut();
        console.log('[RevenueCat] User logged out');
    } catch (error) {
        console.error('[RevenueCat] Logout failed:', error);
    }
}

/**
 * Get available offerings/packages from RevenueCat.
 */
export async function getAvailablePackages(): Promise<PurchasesPackage[]> {
    if (!isRevenueCatInitialized) return [];

    try {
        const offerings: PurchasesOfferings = await Purchases.getOfferings();
        const allPackages: PurchasesPackage[] = [];

        // Collect packages from all offerings
        if (offerings.all) {
            for (const [offeringId, offering] of Object.entries(offerings.all)) {
                for (const pkg of offering.availablePackages) {
                    allPackages.push(pkg);
                }
            }
        }

        console.log('[RevenueCat] Available packages:', allPackages.map(p => ({
            id: p.identifier,
            product: p.product.identifier,
            price: p.product.priceString,
        })));
        return allPackages;
    } catch (error) {
        console.error('[RevenueCat] Get packages failed:', error);
        return [];
    }
}

/**
 * Find the right package for a given plan tier.
 * Searches by offering ID first, then falls back to product identifier matching.
 */
async function findPackageForPlan(plan: PlanTier): Promise<PurchasesPackage | null> {
    const config = PLAN_CONFIG[plan];
    if (!config) return null;

    try {
        const offerings: PurchasesOfferings = await Purchases.getOfferings();

        // Strategy 1: Look for a specific offering for this plan
        const planOffering = offerings.all?.[config.offeringId];
        if (planOffering && planOffering.availablePackages.length > 0) {
            console.log(`[RevenueCat] Found offering "${config.offeringId}" with ${planOffering.availablePackages.length} packages`);
            // Prefer monthly package, fallback to first
            return planOffering.availablePackages.find(p =>
                p.packageType === 'MONTHLY' || p.identifier === '$rc_monthly'
            ) || planOffering.availablePackages[0];
        }

        // Strategy 2: Search all offerings for a matching product identifier
        const allPackages: PurchasesPackage[] = [];
        if (offerings.all) {
            for (const offering of Object.values(offerings.all)) {
                allPackages.push(...offering.availablePackages);
            }
        }
        // Also check default/current offering
        if (offerings.current?.availablePackages) {
            allPackages.push(...offerings.current.availablePackages);
        }

        const matchingPkg = allPackages.find(pkg =>
            config.productIds.includes(pkg.product.identifier)
        );

        if (matchingPkg) {
            console.log(`[RevenueCat] Found product "${matchingPkg.product.identifier}" for plan "${plan}"`);
            return matchingPkg;
        }

        // Strategy 3: If only one offering exists (simple setup), use it for starter
        if (plan === 'starter' && offerings.current?.availablePackages?.length) {
            const monthly = offerings.current.availablePackages.find(p =>
                p.packageType === 'MONTHLY' || p.identifier === '$rc_monthly'
            );
            if (monthly) {
                console.log('[RevenueCat] Fallback: using current offering monthly for starter');
                return monthly;
            }
        }

        console.warn(`[RevenueCat] No package found for plan "${plan}". Available offerings:`,
            Object.keys(offerings.all || {}));
        return null;
    } catch (error) {
        console.error('[RevenueCat] Error finding package:', error);
        return null;
    }
}

/**
 * Purchase a subscription plan via native store.
 */
export async function purchaseSubscription(
    plan: PlanTier,
): Promise<{ success: boolean; plan?: PlanTier; customerInfo?: CustomerInfo; error?: string }> {
    if (!isRevenueCatInitialized) {
        return {
            success: false,
            error: 'El sistema de pagos no está inicializado. Reinicia la app e inténtalo de nuevo.'
        };
    }

    try {
        const targetPackage = await findPackageForPlan(plan);

        if (!targetPackage) {
            return {
                success: false,
                error: `El plan "${plan}" no está disponible en este momento. Por favor contacta soporte.`
            };
        }

        console.log('[RevenueCat] Purchasing:', plan, '→', targetPackage.product.identifier, targetPackage.product.priceString);

        // Execute purchase
        const { customerInfo } = await Purchases.purchasePackage({ aPackage: targetPackage });

        // Check if any of our entitlements are now active
        const config = PLAN_CONFIG[plan];
        const entitlement = config ? customerInfo.entitlements.active[config.entitlementId] : null;
        if (entitlement) {
            console.log('[RevenueCat] Purchase successful! Entitlement active:', config.entitlementId);
        } else {
            console.warn('[RevenueCat] Purchase completed but entitlement not immediately active — webhook may sync later');
        }

        return { success: true, plan, customerInfo };
    } catch (error: any) {
        // Handle user cancellation
        if (error?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
            return { success: false, error: 'Compra cancelada' };
        }

        // Handle store issues
        if (error?.code === PURCHASES_ERROR_CODE.STORE_PROBLEM_ERROR) {
            return { success: false, error: 'Error en la tienda. Inténtalo de nuevo.' };
        }

        console.error('[RevenueCat] Purchase failed:', error);
        return { success: false, error: error?.message || 'Error en la compra' };
    }
}

/**
 * Purchase the single-book consumable product.
 */
export async function purchaseSingleBook(): Promise<{ success: boolean; error?: string }> {
    if (!isRevenueCatInitialized) {
        return { success: false, error: 'Sistema de pagos no inicializado.' };
    }

    try {
        const offerings: PurchasesOfferings = await Purchases.getOfferings();
        // Collect packages from all offerings
        const allPackages: PurchasesPackage[] = [];
        if (offerings.all) {
            for (const offering of Object.values(offerings.all)) {
                allPackages.push(...offering.availablePackages);
            }
        }
        if (offerings.current?.availablePackages) {
            allPackages.push(...offerings.current.availablePackages);
        }

        // Find the single book package by product ID or package type
        const singleBookPackage = allPackages.find(pkg =>
            pkg.product.identifier === 'YourNovel_Single_Book' ||
            pkg.packageType === 'CUSTOM' ||
            pkg.packageType === 'LIFETIME'
        );

        if (!singleBookPackage) {
            console.warn('[RevenueCat] Single book package not found. Available:', allPackages.map(p => p.identifier));
            return { success: false, error: 'El producto de libro único no está disponible.' };
        }

        console.log('[RevenueCat] Purchasing single book:', singleBookPackage.identifier);
        const { customerInfo } = await Purchases.purchasePackage({ aPackage: singleBookPackage });

        console.log('[RevenueCat] Single book purchase successful');
        return { success: true };
    } catch (error: any) {
        if (error?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
            return { success: false, error: 'Compra cancelada' };
        }
        console.error('[RevenueCat] Single book purchase failed:', error);
        return { success: false, error: error?.message || 'Error en la compra' };
    }
}

/**
 * Check current entitlements — returns the user's highest active plan.
 */
export async function checkEntitlements(): Promise<{ plan: PlanTier | null; isActive: boolean }> {
    if (!isRevenueCatInitialized) return { plan: null, isActive: false };

    try {
        const { customerInfo } = await Purchases.getCustomerInfo();
        const activeEntitlements = customerInfo.entitlements.active;

        // Check all entitlements, return the highest-tier active plan
        // Priority: agency > professional > starter
        const tierPriority: PlanTier[] = ['agency', 'professional', 'starter'];
        for (const tier of tierPriority) {
            const config = PLAN_CONFIG[tier];
            if (config && activeEntitlements[config.entitlementId]) {
                console.log('[RevenueCat] Active entitlement:', config.entitlementId, '→', tier);
                return { plan: tier, isActive: true };
            }
        }

        // No active entitlement
        return { plan: null, isActive: false };
    } catch (error) {
        console.error('[RevenueCat] Entitlement check failed:', error);
        return { plan: null, isActive: false };
    }
}

/**
 * Restore previous purchases (required by App Store guidelines).
 */
export async function restorePurchases(): Promise<{ success: boolean; plan?: PlanTier; error?: string }> {
    if (!isRevenueCatInitialized) {
        return { success: false, error: 'Sistema de pagos no inicializado.' };
    }

    try {
        const { customerInfo } = await Purchases.restorePurchases();
        const activeEntitlements = customerInfo.entitlements.active;

        // Find the highest-tier restored entitlement
        const tierPriority: PlanTier[] = ['agency', 'professional', 'starter'];
        for (const tier of tierPriority) {
            const config = PLAN_CONFIG[tier];
            if (config && activeEntitlements[config.entitlementId]) {
                console.log('[RevenueCat] Restored entitlement:', config.entitlementId, '→', tier);
                return { success: true, plan: tier };
            }
        }

        return { success: true }; // Restored but no active entitlements
    } catch (error: any) {
        console.error('[RevenueCat] Restore failed:', error);
        return { success: false, error: error?.message || 'Error al restaurar compras' };
    }
}

/**
 * Main entry point — routes to native or web payment based on platform.
 */
export async function purchasePlan(
    plan: PlanTier,
    _userId: string
): Promise<{ success: boolean; plan?: PlanTier; error?: string }> {
    if (isMobilePlatform()) {
        if (plan === 'single_book') {
            return purchaseSingleBook();
        }
        return purchaseSubscription(plan);
    }

    // Web: handled separately by stripeService.ts
    return { success: false, error: 'Use Stripe for web purchases' };
}
